import Vue from 'vue';
import _ from 'lodash';
import { $http } from '@/utils/https';
import type { ActionContext } from 'vuex';
import type { RootState } from '@/store';
import type { BasketProduct, Product, ProductPrice } from '@/types/shop';
import { storage } from '@/utils/storage';
import { ecommerceRemoveFromCartEvent } from '@/helpers';
import { objectDeepToString } from '@/helpers/variables';

const saveBasketProducts = (products: Array<BasketProduct>): void => {
  const parsed = JSON.stringify(products);
  storage.setItem('products', parsed);
};

const loadBasketProducts = (): Array<BasketProduct> => {
  if (storage.getItem('products')) {
    const products = JSON.parse(storage.getItem('products') || '[]');

    return _.filter(products);
  }

  return [];
};

// state
interface State {
  buyLoading: boolean;
  promoCode: string | null;
  discount: any;
  loadingPromoCode: boolean;
  productsWithDefaultPrices: Array<Product> | null;
  basket: Array<BasketProduct>;
  basketResponse: any;
  buyTogetherProducts: Array<Product>;
  selectedBonuses: selectedBonuses;
}

interface selectedBonuses {
  pay_bonus_amount: number;
}

interface AddBasketPayload {
  select_count: number;
  product_price: ProductPrice;
  product: Product;
  discount?: number;
  unauthorizedProductPriceId: number;
}

interface SetCountPayload {
  product: BasketProduct;
  select_count: number;
}

interface HandlePayload {
  basketProduct: BasketProduct;
  product: Product;
  productPrice: ProductPrice;
}

interface FindProductData {
  basketProduct: BasketProduct;
  product: Product;
  productPrice: ProductPrice;
}

const state: State = {
  buyLoading: false,
  promoCode: '',
  discount: null,
  loadingPromoCode: false,
  productsWithDefaultPrices: null,
  basket: loadBasketProducts(),
  basketResponse: null,
  buyTogetherProducts: [],
  selectedBonuses: {
    pay_bonus_amount: 0
  }
};

// getters
const getters = {
  selectedBonuses: (state: State) => state.selectedBonuses,
  buyTogetherProducts: (state: State) => state.buyTogetherProducts,
  productsWithDefaultPrices: (state: State) => state.productsWithDefaultPrices,

  promoCode: (state: State) => state.promoCode,
  discount: (state: State) => state.discount,
  buyLoading: (state: State) => state.buyLoading,
  loadingPromoCode: (state: State) => state.loadingPromoCode,
  basket: (state: State) => state.basket || [],
  basketResponse: (state: State) => state.basketResponse
};

// actions
const actions = {
  GET_BUY_TOGETHER_PRODUCT: async (
    { commit }: ActionContext<State, RootState>,
    payload: {
      ids: Array<number | string>;
    }
  ) => {
    try {
      const response = await $http.get('v1/basket/products/buy-with-in-basket', {
        params: {
          products: payload.ids
        }
      });
      commit('SET_BUY_TOGETHER_PRODUCT', response.data.data);
      // return response.data;
    } catch (e) {
      throw e;
    }
  },
  GET_SYNC_BASKET: async (
    { commit }: ActionContext<State, RootState>,
    payload: {
      id: string | number;
      select_count: number;
    }[]
  ) => {
    commit('CHECK_PROMO_CODE_LOADING', true);
    try {
      let stringParams = objectDeepToString({ ids: payload, client_price: 1 });

      let url = `v1/basket/products?${stringParams}`;
      const response = await $http.get(url);
      commit('SET_BASKET_PRODUCTS', response.data.data);
      commit('SET_BASKET_RESPONSE', response.data.data);
      return response.data;
    } catch (e) {
      throw e;
    } finally {
      commit('CHECK_PROMO_CODE_LOADING', false);
    }
  },
  // eslint-disable-next-line no-unused-vars
  GET_PROMOCODE_INFO: async ({ commit }: ActionContext<State, RootState>, payload: any) => {
    try {
      type TFetchResponse = { products: Array<any>; promocode: { value: string | null; activated: boolean } };

      const response = await $http.post<TFetchResponse>('/v1/checkout/sync-basket', payload);
      const basket: Array<BasketProduct> = [];

      _(state.basket).each((basketProduct: BasketProduct): void => {
        const productData = _.find(response.data.products, ({ id, productPrice: { value } }) => {
          return basketProduct.id === id && basketProduct.select_type.value === value;
        });

        if (!productData) return;

        basket.push(
          makeBasketProduct({
            product: productData.product,
            product_price: productData.productPrice,
            select_count: basketProduct.select_count || 1,
            discount: productData.discount || 0,
            unauthorizedProductPriceId: basketProduct.unauthorizedProductPriceId
          })
        );
      });

      state.basket = basket;
      saveBasketProducts(state.basket);

      return response.data;
    } catch (e) {
      throw e;
    }
  }
};

const reloadBasketProducts = (state: State) => {
  const newBasket = loadBasketProducts();

  if (newBasket.length !== state.basket.length) {
    state.basket = newBasket;
  }
};

const findProductIndex = (list: Array<BasketProduct>, basketProduct: BasketProduct): number => {
  return _.findIndex(
    list,
    ({ id, select_type }) => id === basketProduct.id && select_type.id === basketProduct.select_type.id
  );
};

const replaceProduct = (basketProduct: BasketProduct): Array<BasketProduct> => {
  const basketProducts = state.basket;

  const index = findProductIndex(basketProducts, basketProduct);
  if (index !== -1) {
    basketProducts[index] = basketProduct;
  } else {
    basketProducts.push(basketProduct);
  }

  return basketProducts;
};

const resolveProductCount = (basketProduct: BasketProduct, quantity: number | any): BasketProduct => {
  basketProduct.select_count = 0;

  const setQuantity = Math.min(
    Math.max(Number.parseFloat(quantity || '1'), 1),
    basketProduct.select_type?.quantity || 1
  );

  console.log('resolveProductCount', { basketProduct, quantity, setQuantity });
  basketProduct.select_count = Math.ceil(setQuantity);

  return basketProduct;
};

const updateCountForProduct = (payload: SetCountPayload): Array<BasketProduct> => {
  let basketProductsList = state.basket;

  let index = findProductIndex(basketProductsList, payload.product);
  if (index !== -1) {
    console.log('index', index);
    basketProductsList[index] = resolveProductCount(basketProductsList[index], payload.select_count);
  }

  console.log('basketProductsList', basketProductsList);

  return basketProductsList;
};

const makeBasketProduct = (payload: AddBasketPayload): BasketProduct => {
  return resolveProductCount(
    {
      id: payload.product.id,
      title: payload.product.title,
      uaTitle: payload.product.uaTitle,
      slug: payload.product.slug,
      image: payload.product.image,
      productPrices: payload.product.productPrices,
      type: payload.product.type,

      select_count: payload.select_count || 1,
      select_type: payload.product_price,
      client_price: undefined,
      client_with_code_price: undefined,
      discount: payload.discount,
      unauthorizedProductPriceId: payload.unauthorizedProductPriceId,
      // @ts-ignore
      brand: payload.product?.brand || null,
      // @ts-ignore
      brandSeries: payload.product?.brandSeries || null
    },
    payload.select_count
  );
};

const findProduct = (products: Array<Product>, basketProduct: BasketProduct): FindProductData | null => {
  let product = _.find(products, ({ id }) => basketProduct.id === id);
  if (!product) return null;

  let productPrice = _.find(product.productPrices.data, ({ id }) => basketProduct.select_type.id === id);
  if (!productPrice) return null;

  return { basketProduct, product, productPrice };
};

const addProductToBasket = (state: State, payload: AddBasketPayload): Array<BasketProduct> => {
  if (payload.product.hasDuplicate) {
    return state.basket;
  }

  let basketProduct = state.basket.find(
    (basketProduct) =>
      basketProduct.id === payload.product.id &&
      basketProduct.select_type.id === payload.product_price.id &&
      basketProduct.select_type.value === payload.product_price.value
  );

  console.log('addProductToBasket', { basketProduct, payload });

  if (basketProduct) {
    basketProduct = resolveProductCount(basketProduct, basketProduct.select_count + payload.select_count);
  } else {
    basketProduct = makeBasketProduct(payload);
  }

  return replaceProduct(basketProduct);
};

setInterval(() => {
  reloadBasketProducts(state);
}, 10000);

// mutations
const mutations = {
  ADD_ITEM_TO_BASKET(state: State, payload: AddBasketPayload) {
    console.log('ADD_ITEM_TO_BASKET', payload);
    state.basket = addProductToBasket(state, payload);

    saveBasketProducts(state.basket);
  },
  ADD_WEBINAR_ITEM_TO_BASKET(state: State, payload: AddBasketPayload) {
    state.basket = addProductToBasket(state, payload);

    saveBasketProducts(state.basket);
  },
  REMOVE_ITEM_FROM_BASKET(state: State, product: BasketProduct) {
    const foundProduct = state.basket.find((productInBasket) => productInBasket.id === product.id);
    if (foundProduct) {
      let { select_type, productPrices, title, brand, brandSeries } = product;

      ecommerceRemoveFromCartEvent({
        item_id: select_type?.id || productPrices.data[0].id,
        item_name: title,
        item_brand: brandSeries?.title || 'Renew',
        item_category: brand?.title || '',
        price: select_type?.price || productPrices.data[0].price
      });
    }

    Vue.delete(state.basket, findProductIndex(state.basket, product));
    saveBasketProducts(state.basket);
  },
  RESET_BASKET(state: State) {
    state.basket = [];

    saveBasketProducts(state.basket);
  },
  CHECK_PROMO_CODE_LOADING(state: State, status: boolean) {
    state.loadingPromoCode = status;
  },
  INCREMENT_BASKET_LIST_COUNT(state: State, basketProduct: BasketProduct) {
    state.basket = updateCountForProduct({
      select_count: basketProduct.select_count + 1,
      product: basketProduct
    });

    console.log('state.basket', state.basket, basketProduct.select_count, basketProduct.select_count + 1);

    saveBasketProducts(state.basket);
  },
  SET_BASKET_LIST_COUNT(state: State, countPayload: SetCountPayload) {
    state.basket = updateCountForProduct(countPayload);

    saveBasketProducts(state.basket);
  },
  DECREMENT_BASKET_LIST_COUNT(state: State, basketProduct: BasketProduct) {
    state.basket = updateCountForProduct({
      select_count: basketProduct.select_count - 1,
      product: basketProduct
    });

    saveBasketProducts(state.basket);
  },
  SET_PROMO_CODE_IN_BASKET(state: State, code: string | null) {
    state.promoCode = code;
  },
  SET_DISCOUNT(state: State, discount: any) {
    state.discount = discount;
  },
  RESET_DISCOUNT(state: State) {
    state.discount = null;
  },
  SET_BASKET_PRODUCTS(state: State, products: Array<Product>) {
    let basket: Array<BasketProduct> = [];

    _(state.basket).each((basketProduct: BasketProduct): void => {
      let productData = findProduct(products, basketProduct);
      //#todo if product not found - not add in new basket list
      //maybe keep it another list for show
      if (!productData) return;

      basket.push(
        makeBasketProduct({
          product: productData.product,
          product_price: productData.productPrice,
          select_count: basketProduct.select_count || 1,
          unauthorizedProductPriceId: basketProduct.unauthorizedProductPriceId
        })
      );
    });
    state.basket = basket;
    saveBasketProducts(state.basket);
  },

  UPDATE_BASKET_PRODUCTS_PRICES(state: State, products: Array<Product>) {
    let basket = state.basket.map((basketProduct: BasketProduct) => {
      let productData = findProduct(products, basketProduct);

      if (productData) {
        let { product, productPrice } = productData;
        basketProduct.select_type = productPrice;
        basketProduct.productPrices.data = product.productPrices.data;
      }

      return basketProduct;
    });

    state.basket = basket;
    saveBasketProducts(state.basket);
  },

  SET_BASKET_RESPONSE(state: State, products: Array<Product>) {
    state.basketResponse = products;
  },
  SET_BUY_TOGETHER_PRODUCT(state: State, data: Array<Product>) {
    state.buyTogetherProducts = data;
  },
  SET_USER_BONUSES(state: State, data: selectedBonuses) {
    state.selectedBonuses = data;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
